import {Controller} from "@hotwired/stimulus";
import {isEmpty} from "lodash";
import { getCsrfToken } from "shared/form_helpers";

// Connects to data-controller="bundle-deployment"
export default class extends Controller {
    static targets = [
        "form",
        "bundleDetails",
        "bundleIcon",
        "bundleName",
        "bundleDescription",
        "deviceTypesList",
        "partsList",
        "locationFields",
        "regionSelect",
        "buildingSelect",
        "areaSelect",
        "roomSelect",
        "deployButton",
        "resetButton",
        "deploymentPreview",
        "targetLocation",
        "locationPath",
        "previewDeviceTypes",
        "previewParts",
        "totalItemsCount"
    ]

    connect() {
        this.initializeForm();
        this.setupEventListeners();
    }

    disconnect() {
        this.removeEventListeners();
    }

    initializeForm() {
        // Initialize location state tracking
        this.selectedRegion = null;
        this.selectedBuilding = null;
        this.selectedArea = null;
        this.selectedRoom = null;

        // Initialize bundle data tracking
        this.selectedBundleData = null;

        // Initially disable location fields and deploy button
        this.disableLocationFields();
        this.disableDeployButton();

        // Hide bundle details and deployment preview sections
        this.bundleDetailsTarget.style.display = 'none';
        this.deploymentPreviewTarget.style.display = 'none';
    }

    setupEventListeners() {
        // Listen for bundle selection events from the bundle-select controller
        this.boundBundleSelectionMade = this.handleBundleSelectionMade.bind(this);
        this.boundBundleSelectionCleared = this.handleBundleSelectionCleared.bind(this);
        this.boundRegionSelectionMade = this.handleRegionSelectionMade.bind(this);
        this.boundBuildingSelectionMade = this.handleBuildingSelectionMade.bind(this);
        this.boundAreaSelectionMade = this.handleAreaSelectionMade.bind(this);
        this.boundRoomSelectionMade = this.handleRoomSelectionMade.bind(this);
        this.boundLocationSelectionCleared = this.handleLocationSelectionCleared.bind(this);

        window.addEventListener('bundleSelectionMade', this.boundBundleSelectionMade);
        window.addEventListener('bundleSelectionCleared', this.boundBundleSelectionCleared);
        window.addEventListener('regionSelectionMade', this.boundRegionSelectionMade);
        window.addEventListener('buildingSelectionMade', this.boundBuildingSelectionMade);
        window.addEventListener('areaSelectionMade', this.boundAreaSelectionMade);
        window.addEventListener('roomSelectionMade', this.boundRoomSelectionMade);
        window.addEventListener('regionSelectionCleared', this.boundLocationSelectionCleared);
        window.addEventListener('buildingSelectionCleared', this.boundLocationSelectionCleared);
        window.addEventListener('areaSelectionCleared', this.boundLocationSelectionCleared);
        window.addEventListener('roomSelectionCleared', this.boundLocationSelectionCleared);
    }

    removeEventListeners() {
        window.removeEventListener('bundleSelectionMade', this.boundBundleSelectionMade);
        window.removeEventListener('bundleSelectionCleared', this.boundBundleSelectionCleared);
        window.removeEventListener('regionSelectionMade', this.boundRegionSelectionMade);
        window.removeEventListener('buildingSelectionMade', this.boundBuildingSelectionMade);
        window.removeEventListener('areaSelectionMade', this.boundAreaSelectionMade);
        window.removeEventListener('roomSelectionMade', this.boundRoomSelectionMade);
        window.removeEventListener('regionSelectionCleared', this.boundLocationSelectionCleared);
        window.removeEventListener('buildingSelectionCleared', this.boundLocationSelectionCleared);
        window.removeEventListener('areaSelectionCleared', this.boundLocationSelectionCleared);
        window.removeEventListener('roomSelectionCleared', this.boundLocationSelectionCleared);
    }

    handleBundleSelectionMade(event) {
        const bundleData = event.detail;

        // Store bundle data for preview
        this.selectedBundleData = bundleData;

        // Show bundle details
        this.displayBundleDetails(bundleData);

        // Enable location fields
        this.enableLocationFields();

        // Update deployment preview
        this.updateDeploymentPreview();

        // Check if we can enable deploy button
        this.updateDeployButtonState();
    }

    handleBundleSelectionCleared(event) {
        // Clear bundle data
        this.selectedBundleData = null;

        // Hide bundle details and deployment preview
        this.bundleDetailsTarget.style.display = 'none';
        this.deploymentPreviewTarget.style.display = 'none';

        // Disable location fields
        this.disableLocationFields();

        // Disable deploy button
        this.disableDeployButton();

        // Clear location selections
        this.clearLocationSelections();
    }

    handleRegionSelectionMade(event) {
        this.selectedRegion = event.detail.region;
        this.selectedBuilding = null;
        this.selectedArea = null;
        this.selectedRoom = null;
        this.updateDeploymentPreview();
        this.updateDeployButtonState();
    }

    handleBuildingSelectionMade(event) {
        this.selectedBuilding = event.detail.building;
        this.selectedArea = null;
        this.selectedRoom = null;
        this.updateDeploymentPreview();
        this.updateDeployButtonState();
    }

    handleAreaSelectionMade(event) {
        this.selectedArea = event.detail.area;
        this.selectedRoom = null;
        this.updateDeploymentPreview();
        this.updateDeployButtonState();
    }

    handleRoomSelectionMade(event) {
        this.selectedRoom = event.detail.room;
        this.updateDeploymentPreview();
        this.updateDeployButtonState();
    }

    handleLocationSelectionCleared(event) {
        this.selectedRegion = null;
        this.selectedBuilding = null;
        this.selectedArea = null;
        this.selectedRoom = null;
        this.updateDeploymentPreview();
        this.disableDeployButton();
    }

    displayBundleDetails(bundleData) {
        // Display bundle icon
        if (bundleData.icon) {
            this.bundleIconTarget.innerHTML = `<i class="${bundleData.icon} fa-3x text-primary"></i>`;
        } else {
            this.bundleIconTarget.innerHTML = `<i class="fas fa-cube fa-3x text-primary"></i>`;
        }

        // Display bundle name
        this.bundleNameTarget.textContent = bundleData.name || 'Unknown Bundle';

        // Display bundle description (if available)
        this.bundleDescriptionTarget.textContent = bundleData.description || 'No description available';

        // Display device types
        this.displayDeviceTypes(bundleData.models || []);

        // Display parts
        this.displayParts(bundleData.parts || []);

        // Show the bundle details section
        this.bundleDetailsTarget.style.display = 'block';
    }

    displayDeviceTypes(models) {
        if (isEmpty(models)) {
            this.deviceTypesListTarget.innerHTML = '<li class="text-muted">No device types</li>';
            return;
        }

        const modelItems = models.map(model => {
            // Handle both object format (with text/name) and ID-only format
            const displayName = model.text || model.name || model || 'Unknown Device Type';
            return `<li><i class="fas fa-desktop me-2"></i>${displayName}</li>`;
        }).join('');

        this.deviceTypesListTarget.innerHTML = modelItems;
    }

    displayParts(parts) {
        if (isEmpty(parts)) {
            this.partsListTarget.innerHTML = '<li class="text-muted">No parts</li>';
            return;
        }

        const partItems = parts.map(part => {
            // Handle both object format (with text/name) and ID-only format
            const displayName = part.text || part.name || part || 'Unknown Part';
            return `<li><i class="fas fa-cog me-2"></i>${displayName}</li>`;
        }).join('');

        this.partsListTarget.innerHTML = partItems;
    }

    enableLocationFields() {
        this.regionSelectTarget.disabled = false;
        // Building, area, and room selects will be enabled by their respective controllers
        // when parent selections are made

        // Also ensure the TomSelect instance is enabled if it exists
        if (this.regionSelectTarget.tomselect) {
            this.regionSelectTarget.tomselect.enable();
        }
    }

    disableLocationFields() {
        this.regionSelectTarget.disabled = true;
        this.buildingSelectTarget.disabled = true;
        this.areaSelectTarget.disabled = true;
        this.roomSelectTarget.disabled = true;

        // Also disable TomSelect instances if they exist
        if (this.regionSelectTarget.tomselect) {
            this.regionSelectTarget.tomselect.disable();
        }
        if (this.buildingSelectTarget.tomselect) {
            this.buildingSelectTarget.tomselect.disable();
        }
        if (this.areaSelectTarget.tomselect) {
            this.areaSelectTarget.tomselect.disable();
        }
        if (this.roomSelectTarget.tomselect) {
            this.roomSelectTarget.tomselect.disable();
        }
    }

    clearLocationSelections() {
        // Clear all location selections
        if (this.regionSelectTarget.tomselect) {
            this.regionSelectTarget.tomselect.clear(true);
        }
        if (this.buildingSelectTarget.tomselect) {
            this.buildingSelectTarget.tomselect.clear(true);
        }
        if (this.areaSelectTarget.tomselect) {
            this.areaSelectTarget.tomselect.clear(true);
        }
        if (this.roomSelectTarget.tomselect) {
            this.roomSelectTarget.tomselect.clear(true);
        }
    }

    updateDeploymentPreview() {
        if (!this.selectedBundleData) {
            this.deploymentPreviewTarget.style.display = 'none';
            return;
        }

        // Update location path
        this.updateLocationPath();

        // Update items preview
        this.updateItemsPreview();

        // Show deployment preview if bundle is selected
        this.deploymentPreviewTarget.style.display = 'block';
    }

    updateLocationPath() {
        const locationParts = [];

        // Get the display text from the TomSelect controls instead of relying on event data
        if (this.regionSelectTarget.tomselect && this.regionSelectTarget.value) {
            const regionOption = this.regionSelectTarget.tomselect.options[this.regionSelectTarget.value];
            if (regionOption) {
                locationParts.push(regionOption.text);
            }
        }

        if (this.buildingSelectTarget.tomselect && this.buildingSelectTarget.value) {
            const buildingOption = this.buildingSelectTarget.tomselect.options[this.buildingSelectTarget.value];
            if (buildingOption) {
                locationParts.push(buildingOption.text);
            }
        }

        if (this.areaSelectTarget.tomselect && this.areaSelectTarget.value) {
            const areaOption = this.areaSelectTarget.tomselect.options[this.areaSelectTarget.value];
            if (areaOption) {
                locationParts.push(areaOption.text);
            }
        }

        if (this.roomSelectTarget.tomselect && this.roomSelectTarget.value) {
            const roomOption = this.roomSelectTarget.tomselect.options[this.roomSelectTarget.value];
            if (roomOption) {
                locationParts.push(roomOption.text);
            }
        }

        const locationPath = locationParts.length > 0 ? locationParts.join(' → ') : 'No location selected';

        this.locationPathTarget.textContent = locationPath;
    }

    updateItemsPreview() {
        if (!this.selectedBundleData) return;

        const models = this.selectedBundleData.models || [];
        const parts = this.selectedBundleData.parts || [];

        // Update device types preview with counts
        this.updateDeviceTypesPreview(models);

        // Update parts preview with counts
        this.updatePartsPreview(parts);

        // Update total count
        const totalItems = models.length + parts.length;
        this.totalItemsCountTarget.textContent = totalItems;
    }

    updateDeviceTypesPreview(models) {
        if (isEmpty(models)) {
            this.previewDeviceTypesTarget.innerHTML = '<p class="text-muted mb-0">No device types</p>';
            return;
        }

        // Count occurrences of each device type
        const deviceTypeCounts = {};
        models.forEach(model => {
            // Handle both object format (with text/name) and ID-only format
            const name = model.text || model.name || model || 'Unknown Device Type';
            deviceTypeCounts[name] = (deviceTypeCounts[name] || 0) + 1;
        });

        const deviceTypeItems = Object.entries(deviceTypeCounts).map(([name, count]) => {
            return `
        <div class="d-flex justify-content-between align-items-center py-1">
          <span><i class="fas fa-desktop me-2 text-primary"></i>${name}</span>
          <span class="badge bg-secondary">${count}</span>
        </div>
      `;
        }).join('');

        this.previewDeviceTypesTarget.innerHTML = deviceTypeItems;
    }

    updatePartsPreview(parts) {
        if (isEmpty(parts)) {
            this.previewPartsTarget.innerHTML = '<p class="text-muted mb-0">No parts</p>';
            return;
        }

        // Count occurrences of each part type
        const partTypeCounts = {};
        parts.forEach(part => {
            // Handle both object format (with text/name) and ID-only format
            const name = part.text || part.name || part || 'Unknown Part';
            partTypeCounts[name] = (partTypeCounts[name] || 0) + 1;
        });

        const partItems = Object.entries(partTypeCounts).map(([name, count]) => {
            return `
        <div class="d-flex justify-content-between align-items-center py-1">
          <span><i class="fas fa-cog me-2 text-success"></i>${name}</span>
          <span class="badge bg-secondary">${count}</span>
        </div>
      `;
        }).join('');

        this.previewPartsTarget.innerHTML = partItems;
    }


    updateDeployButtonState() {
        const isLocationComplete = this.isLocationComplete();
        const isBundleSelected = this.bundleDetailsTarget.style.display !== 'none';

        if (isBundleSelected && isLocationComplete) {
            this.enableDeployButton();
        } else {
            this.disableDeployButton();
        }
    }

    isLocationComplete() {
        // Check both form field values and tracked state for comprehensive validation
        const formFieldsComplete = (this.regionSelectTarget.value && this.buildingSelectTarget.value && this.areaSelectTarget.value && this.roomSelectTarget.value);

        const trackedStateComplete = (this.selectedRegion && this.selectedBuilding && this.selectedArea && this.selectedRoom);

        return formFieldsComplete || trackedStateComplete;
    }

    enableDeployButton() {
        this.deployButtonTarget.disabled = false;
    }

    disableDeployButton() {
        this.deployButtonTarget.disabled = true;
    }

    resetForm() {
        // Clear bundle selection
        const bundleSelect = document.getElementById('bundle-select');
        if (bundleSelect && bundleSelect.tomselect) {
            bundleSelect.tomselect.clear(true);
        }

        // Clear location selections
        this.clearLocationSelections();

        // Reset location state tracking
        this.selectedRegion = null;
        this.selectedBuilding = null;
        this.selectedArea = null;
        this.selectedRoom = null;

        // Reset bundle data tracking
        this.selectedBundleData = null;

        // Reset form state
        this.initializeForm();
    }

    // Action method for reset button
    reset(event) {
        event.preventDefault();
        this.resetForm();
    }

    // Action method for deploy button
    async deploy(event) {
        event.preventDefault();
        
        if (!this.validateDeployment()) {
            return;
        }

        try {
            this.disableFormDuringDeployment();
            this.showDeploymentProgress();
            
            const deploymentParams = this.buildDeploymentParams();
            const response = await this.submitDeployment(deploymentParams);
            
            if (response.success) {
                this.showSuccessMessage(response.count || response.items?.length || 0);
                this.resetForm();
            } else {
                this.showErrorMessage(response.errors ? response.errors.join(', ') : 'Deployment failed');
            }
        } catch (error) {
            console.error('Deployment error:', error);
            this.showErrorMessage(`Deployment failed: ${error.message}`);
        } finally {
            this.enableFormAfterDeployment();
            this.hideDeploymentProgress();
        }
    }

    validateDeployment() {
        // Check if bundle is selected
        if (!this.selectedBundleData) {
            this.showErrorMessage('Please select a bundle to deploy');
            return false;
        }

        // Check if location is complete
        if (!this.isLocationComplete()) {
            this.showErrorMessage('Please select a complete location (Region → Building → Area → Room)');
            return false;
        }

        return true;
    }

    buildDeploymentParams() {
        return {
            bundle: this.selectedBundleData.id,
            locale: {
                region: this.regionSelectTarget.value,
                building: this.buildingSelectTarget.value,
                area: this.areaSelectTarget.value,
                room: this.roomSelectTarget.value
            },
            name: `Bundle Instance ${Date.now()}`,
            deployment_status: 'active'
        };
    }

    async submitDeployment(params) {
        const response = await fetch('/api/items', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrfToken(),
                'Accept': 'application/json'
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    }

    disableFormDuringDeployment() {
        this.deployButtonTarget.disabled = true;
        this.resetButtonTarget.disabled = true;
        
        // Disable all form controls
        this.disableLocationFields();
        
        // Disable bundle select if it exists
        const bundleSelect = document.getElementById('bundle-select');
        if (bundleSelect && bundleSelect.tomselect) {
            bundleSelect.tomselect.disable();
        }
    }

    enableFormAfterDeployment() {
        this.resetButtonTarget.disabled = false;
        
        // Re-enable form controls based on current state
        if (this.selectedBundleData) {
            this.enableLocationFields();
        }
        
        // Re-enable bundle select
        const bundleSelect = document.getElementById('bundle-select');
        if (bundleSelect && bundleSelect.tomselect) {
            bundleSelect.tomselect.enable();
        }
        
        // Deploy button will be re-enabled by updateDeployButtonState if conditions are met
        this.updateDeployButtonState();
    }

    showDeploymentProgress() {
        // Update deploy button to show progress
        const originalText = this.deployButtonTarget.textContent;
        this.deployButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Deploying...';
        this.deployButtonTarget.setAttribute('data-original-text', originalText);
    }

    hideDeploymentProgress() {
        // Restore original button text
        const originalText = this.deployButtonTarget.getAttribute('data-original-text');
        if (originalText) {
            this.deployButtonTarget.textContent = originalText;
            this.deployButtonTarget.removeAttribute('data-original-text');
        }
    }

    showSuccessMessage(itemCount) {
        this.showFlashMessage('success', `Successfully deployed bundle! Created ${itemCount} items.`);
    }

    showErrorMessage(message) {
        this.showFlashMessage('danger', message);
    }

    showFlashMessage(type, message) {
        // Find or create flash message container
        let flashContainer = document.querySelector('.flash-messages');
        if (!flashContainer) {
            flashContainer = document.createElement('div');
            flashContainer.className = 'flash-messages';
            
            // Insert at the top of the main content area
            const mainContent = document.querySelector('main') || document.querySelector('.container-fluid') || document.body;
            mainContent.insertBefore(flashContainer, mainContent.firstChild);
        }

        // Create flash message element
        const flashMessage = document.createElement('div');
        flashMessage.className = `alert alert-${type} alert-dismissible fade show`;
        flashMessage.innerHTML = `
            ${message}
            <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        `;

        // Add to container
        flashContainer.appendChild(flashMessage);

        // Auto-dismiss success messages after 5 seconds
        if (type === 'success') {
            setTimeout(() => {
                if (flashMessage.parentNode) {
                    flashMessage.remove();
                }
            }, 5000);
        }

        // Scroll to top to ensure message is visible
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
}